import { useCallback, useState } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import AnnotationService from "services/annotations.service"
import { applyUpdatedErefsToCache, insertEcallIntoCache } from "helpers/quotationCache.helper"
import type { TCreateEcallRequest, TCreateEcallResponse, TEcall } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TPaneBinding } from "../core/viewerPanes"
import type { TViewerTool } from "../core/viewerTools"
import { useAreaCreate, type TAreaCreateSession, type TAreaDraft } from "./useAreaCreate"

export type TEcallCreateArea = TAreaDraft
export type TEcallCreateSession = TAreaCreateSession

type DraftRectRef = MutableRefObject<{ element: HTMLElement; handles: HTMLElement[] } | null>

export type EcallCreateContext = {
	createSessionRef: MutableRefObject<TEcallCreateSession | null>
	draftRectRef: DraftRectRef
	appContainerRef: MutableRefObject<HTMLElement | null>
	getViewerPanes: () => TPaneBinding[]
	layoutEpoch: number
	numericQuotationIdRef: MutableRefObject<number | undefined>
	createEcallAsync: (args: {
		quotationId: number
		body: TCreateEcallRequest
	}) => Promise<{ data: TCreateEcallResponse }>
	queryClient: QueryClient
	onEcallCreated: (ecall: TEcall) => void
	onToolChange: (tool: TViewerTool) => void
	clearSelectionToolbar: () => void
	success: (text: string) => void
	formatError: (error: unknown) => void
}

/** Owns the draw-area gesture and create request for a new elevation callout. */
export const useEcallCreate = (ctx: EcallCreateContext) => {
	const {
		createSessionRef,
		draftRectRef,
		appContainerRef,
		getViewerPanes,
		layoutEpoch,
		numericQuotationIdRef,
		createEcallAsync,
		queryClient,
		onEcallCreated,
		onToolChange,
		clearSelectionToolbar,
		success,
		formatError,
	} = ctx
	const { createSession, startCreateArea, cancelCreateArea } = useAreaCreate({
		createSessionRef,
		draftRectRef,
		appContainerRef,
		getViewerPanes,
		layoutEpoch,
		onToolChange,
		clearSelectionToolbar,
	})
	const [isCreatePending, setIsCreatePending] = useState(false)

	const runCreateEcall = useCallback(
		async (
			body: Omit<TCreateEcallRequest, "page_number" | "x0" | "y0" | "x1" | "y1" | "page_name"> & { page_name: string },
		) => {
			const quotationId = numericQuotationIdRef.current
			const area = createSessionRef.current?.area
			if (quotationId == null || !area) return
			setIsCreatePending(true)
			try {
				const { updated_erefs: updatedErefs, ...created } = (
					await createEcallAsync({
						quotationId,
						body: { ...body, page_number: area.pageNumber, ...area.coordinates },
					})
				).data
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
					return applyUpdatedErefsToCache(insertEcallIntoCache(cached, created), updatedErefs)
				})
				AnnotationService.addEcallAnnotation(created)
				cancelCreateArea()
				onEcallCreated(created)
				success("Elevation callout created")
			} catch (error) {
				formatError(error)
			} finally {
				setIsCreatePending(false)
			}
		},
		[
			cancelCreateArea,
			createEcallAsync,
			createSessionRef,
			formatError,
			numericQuotationIdRef,
			onEcallCreated,
			queryClient,
			success,
		],
	)

	return {
		createSession,
		isCreatePending,
		startCreateEcall: startCreateArea,
		cancelCreateEcall: cancelCreateArea,
		runCreateEcall,
	}
}
